import type { ApiClient } from '@/api/client';
import { ApiCallError } from '@/api/client';
import type {
  AnswerResult,
  ChatMessage,
  ContributorView,
  Player,
  QuestionHost,
  QuizEvent,
  RealtimeAuth,
  SessionState,
  StartSessionResponse,
  UpsertQuestionRequest,
} from '@/api/types';
import {
  MOCK_AVATARS,
  MOCK_CHAT,
  MOCK_EVENTS,
  MOCK_HOST_PLAYER,
  MOCK_INVITEES,
  MOCK_LOBBY_STATE,
  MOCK_PLAYERS,
  MOCK_QUESTION_OPEN_STATE,
  MOCK_QUESTIONS,
  MOCK_SESSION_ID,
  MOCK_SESSIONS,
  MOCK_USER,
} from './data';

/**
 * Pure in-memory mock backend. Mutates the same module-scoped state
 * across the SPA so a question added in /host shows up live in /kiosk.
 * Triggered when VITE_USE_MOCK_API=true (default in dev when the
 * proxy target is unreachable).
 */

let events = [...MOCK_EVENTS];
let questions = [...MOCK_QUESTIONS];
const invitees = [...MOCK_INVITEES];
const sessions = [...MOCK_SESSIONS];
let lobbyState: SessionState = structuredClone(MOCK_LOBBY_STATE);
let chat = [...MOCK_CHAT];
const hostPlayer = MOCK_HOST_PLAYER;

const subscribers = new Set<(state: SessionState) => void>();

export function subscribeToSession(fn: (s: SessionState) => void) {
  subscribers.add(fn);
  fn(lobbyState);
  return () => {
    subscribers.delete(fn);
  };
}

function publish(state: SessionState) {
  lobbyState = state;
  for (const s of subscribers) s(state);
}

/**
 * Test/Storybook hook: pre-stage which session state `getSessionState`
 * (and the kiosk/control/player screens that hang off it) returns.
 * Pass `null` to fall back to the default lobby state. Stories use
 * this from a setup decorator before mounting the screen so each
 * screen story can render lobby, question_open, question_review or
 * ended without faking a full play-through.
 */
export function __setMockSessionState(state: SessionState | null): void {
  lobbyState = state ? structuredClone(state) : structuredClone(MOCK_LOBBY_STATE);
}

/**
 * Replace the mock chat thread for a story. `null` resets to the
 * built-in sample messages.
 */
export function __setMockChat(messages: ChatMessage[] | null): void {
  chat = messages ? [...messages] : [...MOCK_CHAT];
}

/**
 * Replace the host events list (the `GET /events` response) for the
 * EventsListScreen stories. Pass `null` to fall back to the default
 * pair of sample events.
 */
export function __setMockEvents(next: QuizEvent[] | null): void {
  events = next ? [...next] : [...MOCK_EVENTS];
}

let contributorOverride: ContributorView | null = null;

/**
 * Override what `GET /c/{token}` returns for the Contributor screen
 * stories. Pass `null` to fall back to the default "opened, no
 * questions" view.
 */
export function __setMockContributorView(view: ContributorView | null): void {
  contributorOverride = view ? structuredClone(view) : null;
}

function notFound(message = 'Not found'): never {
  throw new ApiCallError(404, { code: 'NOT_FOUND', message }, message);
}

const REALTIME_AUTH: RealtimeAuth = {
  endpointHttp: 'mock://appsync',
  endpointRealtime: 'mock://appsync/realtime',
  apiKey: 'mock-key',
  namespace: '/play',
  channels: [`/play/${MOCK_SESSION_ID}/players`, `/play/${MOCK_SESSION_ID}/kiosk`],
  expiresAt: new Date(Date.now() + 3600_000).toISOString(),
  currentVersion: 0,
};

export function createMockClient(): ApiClient {
  return {
    async request({ method, path, body }) {
      await new Promise((r) => setTimeout(r, 40));
      const route = `${method} ${path.replace(/\?.*$/, '')}`;
      switch (true) {
        case route === 'GET /health':
          return { status: 'ok', version: '0.1.0-mock' } as never;

        case route === 'GET /me':
          return MOCK_USER as never;

        case route === 'GET /me/active-session':
          return sessions[0] as never;

        case route === 'GET /events':
          return { items: events } as never;

        case route === 'POST /events': {
          const e = body as QuizEvent;
          const created: QuizEvent = {
            ...e,
            status: 'draft',
            createdAt: new Date().toISOString(),
            questionCount: 0,
            defaultQuestionDurationMs: e.defaultQuestionDurationMs ?? 20000,
            allowContributorDurationOverride: e.allowContributorDurationOverride ?? false,
            shuffle: e.shuffle ?? true,
            revealAuthorship: e.revealAuthorship ?? 'at_end',
            hostRole: e.hostRole ?? 'curator',
            hostPlays: e.hostPlays ?? false,
            theme: e.theme ?? 'default',
            allowImages: e.allowImages ?? true,
          };
          events = [created, ...events];
          return created as never;
        }

        case /^GET \/events\/[^/]+$/.test(route): {
          const id = path.split('/').pop()!;
          const e = events.find((x) => x.id === id);
          if (!e) notFound();
          return e as never;
        }

        case /^PATCH \/events\/[^/]+$/.test(route): {
          const id = path.split('/').pop()!;
          events = events.map((x) => (x.id === id ? { ...x, ...(body as object) } : x));
          return events.find((x) => x.id === id)! as never;
        }

        case /^DELETE \/events\/[^/]+$/.test(route): {
          const id = path.split('/').pop()!;
          events = events.filter((x) => x.id !== id);
          return undefined as never;
        }

        case /^GET \/events\/[^/]+\/questions$/.test(route): {
          const id = path.split('/')[2]!;
          return {
            items: questions.filter((q) => q.eventId === id),
          } as never;
        }

        case /^POST \/events\/[^/]+\/questions$/.test(route): {
          const id = path.split('/')[2]!;
          const req = body as UpsertQuestionRequest;
          const existingIdx = questions.findIndex((q) => q.id === req.id);
          const meta = {
            eventId: id,
            position:
              existingIdx >= 0
                ? questions[existingIdx]!.position
                : questions.filter((q) => q.eventId === id).length,
            createdAt:
              existingIdx >= 0 ? questions[existingIdx]!.createdAt : new Date().toISOString(),
            authorDisplayName: MOCK_USER.displayName,
          };
          const next: QuestionHost =
            req.type === 'quiz'
              ? {
                  id: req.id,
                  type: 'quiz',
                  stem: req.stem,
                  imageUrl: req.imageUrl,
                  durationMsOverride: req.durationMsOverride,
                  options: req.options,
                  ...meta,
                }
              : req.type === 'tf'
                ? {
                    id: req.id,
                    type: 'tf',
                    stem: req.stem,
                    imageUrl: req.imageUrl,
                    durationMsOverride: req.durationMsOverride,
                    options: req.options,
                    ...meta,
                  }
                : req.type === 'match'
                  ? {
                      id: req.id,
                      type: 'match',
                      stem: req.stem,
                      imageUrl: req.imageUrl,
                      durationMsOverride: req.durationMsOverride,
                      pairs: req.pairs,
                      ...meta,
                    }
                  : req.type === 'numeric'
                    ? {
                        id: req.id,
                        type: 'numeric',
                        stem: req.stem,
                        imageUrl: req.imageUrl,
                        durationMsOverride: req.durationMsOverride,
                        numericMin: req.numericMin,
                        numericMax: req.numericMax,
                        numericStep: req.numericStep,
                        numericAnswer: req.numericAnswer,
                        numericUnit: req.numericUnit ?? null,
                        numericTolerancePct: req.numericTolerancePct,
                        ...meta,
                      }
                    : {
                        id: req.id,
                        type: 'poll',
                        stem: req.stem,
                        imageUrl: req.imageUrl,
                        durationMsOverride: req.durationMsOverride,
                        options: req.options,
                        ...meta,
                      };
          if (existingIdx >= 0) {
            const copy = [...questions];
            copy[existingIdx] = next;
            questions = copy;
          } else {
            questions = [...questions, next];
          }
          events = events.map((e) =>
            e.id === id
              ? {
                  ...e,
                  questionCount: questions.filter((q) => q.eventId === id).length,
                }
              : e,
          );
          return next as never;
        }

        case /^DELETE \/events\/[^/]+\/questions\/[^/]+$/.test(route): {
          const qid = path.split('/').pop()!;
          questions = questions.filter((q) => q.id !== qid);
          return undefined as never;
        }

        case /^GET \/events\/[^/]+\/invitees$/.test(route): {
          const id = path.split('/')[2]!;
          const items = invitees.filter((i) => i.eventId === id);
          return {
            items,
            summary: {
              total: items.length,
              pending: items.filter((i) => i.status === 'pending').length,
              opened: items.filter((i) => i.status === 'opened').length,
              submitted: items.filter((i) => i.status === 'submitted').length,
            },
          } as never;
        }

        case /^POST \/events\/[^/]+\/invitees$/.test(route): {
          const id = path.split('/')[2]!;
          const reqBody = body as {
            invitees?: Array<{ name: string; email?: string | null }>;
            emails?: string[];
            sendInvites?: boolean;
          };
          const inputs: Array<{ name: string; email: string | null }> = reqBody.invitees
            ? reqBody.invitees.map((i) => ({ name: i.name, email: i.email ?? null }))
            : (reqBody.emails ?? []).map((e) => ({ name: e, email: e }));
          let created = 0;
          let alreadyExisted = 0;
          let emailsSent = 0;
          const items: Array<{
            invitee: (typeof invitees)[number];
            magicLink: string | null;
          }> = [];
          for (const input of inputs) {
            if (
              input.email &&
              invitees.some((i) => i.eventId === id && i.email === input.email)
            ) {
              const existing = invitees.find(
                (i) => i.eventId === id && i.email === input.email,
              )!;
              items.push({ invitee: existing, magicLink: null });
              alreadyExisted += 1;
              continue;
            }
            const row = {
              id: `inv-${Math.random().toString(36).slice(2, 8)}`,
              eventId: id,
              name: input.name,
              email: input.email,
              status: 'pending' as const,
              createdAt: new Date().toISOString(),
              questionCount: 0,
            };
            invitees.push(row);
            const token = Math.random().toString(36).slice(2, 18);
            items.push({ invitee: row, magicLink: `https://play.example/c/${token}` });
            created += 1;
            if (input.email && reqBody.sendInvites !== false) emailsSent += 1;
          }
          return { items, created, alreadyExisted, emailsSent } as never;
        }

        case /^POST \/events\/[^/]+\/invitees\/[^/]+\/rotate-link$/.test(route): {
          const token = Math.random().toString(36).slice(2, 18);
          return { magicLink: `https://play.example/c/${token}` } as never;
        }

        case /^POST \/events\/[^/]+\/invitees\/[^/]+\/resend$/.test(route): {
          return { queuedAt: new Date().toISOString() } as never;
        }

        case /^DELETE \/events\/[^/]+\/invitees\/[^/]+$/.test(route): {
          const invId = path.split('/').pop()!;
          const idx = invitees.findIndex((i) => i.id === invId);
          if (idx >= 0) invitees.splice(idx, 1);
          return undefined as never;
        }

        case /^GET \/c\/[^/]+$/.test(route): {
          if (contributorOverride) return contributorOverride as never;
          const view: ContributorView = {
            event: {
              title: events[0]!.title,
              subject: events[0]!.subject,
              collectionPrompt: events[0]!.collectionPrompt,
              questionsPerInvitee: events[0]!.questionsPerInvitee ?? 5,
              deadlineAt: events[0]!.deadlineAt,
              allowContributorDurationOverride: events[0]!.allowContributorDurationOverride,
              defaultQuestionDurationMs: events[0]!.defaultQuestionDurationMs,
              status: events[0]!.status,
            },
            invitee: { email: 'lise@family.example', status: 'opened' },
            questions: [],
          };
          return view as never;
        }

        case /^PUT \/c\/[^/]+\/questions$/.test(route): {
          const { questions: qs } = body as {
            questions: QuestionHost[];
          };
          const view: ContributorView = {
            event: {
              title: events[0]!.title,
              subject: events[0]!.subject,
              collectionPrompt: events[0]!.collectionPrompt,
              questionsPerInvitee: events[0]!.questionsPerInvitee ?? 5,
              deadlineAt: events[0]!.deadlineAt,
              allowContributorDurationOverride: events[0]!.allowContributorDurationOverride,
              defaultQuestionDurationMs: events[0]!.defaultQuestionDurationMs,
              status: events[0]!.status,
            },
            invitee: { email: 'lise@family.example', status: 'opened' },
            questions: qs.map((q, i) => ({
              ...q,
              eventId: events[0]!.id,
              position: i,
              createdAt: new Date().toISOString(),
            })) as QuestionHost[],
          };
          return view as never;
        }

        case /^POST \/c\/[^/]+\/questions:submit$/.test(route): {
          const view: ContributorView = {
            event: {
              title: events[0]!.title,
              subject: events[0]!.subject,
              collectionPrompt: events[0]!.collectionPrompt,
              questionsPerInvitee: events[0]!.questionsPerInvitee ?? 5,
              deadlineAt: events[0]!.deadlineAt,
              allowContributorDurationOverride: events[0]!.allowContributorDurationOverride,
              defaultQuestionDurationMs: events[0]!.defaultQuestionDurationMs,
              status: events[0]!.status,
            },
            invitee: {
              email: 'lise@family.example',
              status: 'submitted',
              submittedAt: new Date().toISOString(),
            },
            questions: [],
          };
          return view as never;
        }

        case route === 'GET /avatars':
          return { items: MOCK_AVATARS } as never;

        case /^POST \/events\/[^/]+\/sessions$/.test(route): {
          const eventId = path.split('/')[2]!;
          const event = events.find((e) => e.id === eventId);
          const resp: StartSessionResponse = {
            ...sessions[0]!,
            eventId,
            kioskToken: 'mock-kiosk-token',
            hostPlayer: event?.hostPlays ? hostPlayer : undefined,
          };
          return resp as never;
        }

        case /^GET \/events\/[^/]+\/sessions$/.test(route):
          return { items: sessions } as never;

        case /^GET \/sessions\/by-pin\/[0-9]+$/.test(route): {
          const pin = path.split('/').pop()!;
          // Story hook: a PIN that starts with "9" simulates "no
          // active game with that PIN" so the PinEntryScreen error
          // branch can be exercised in Storybook.
          if (pin.startsWith('9')) notFound('No active game with that PIN.');
          const session = sessions.find((s) => s.pin === pin) ?? sessions[0]!;
          return {
            sessionId: session.id,
            eventTitle: events.find((e) => e.id === session.eventId)?.title ?? '',
            status: session.status,
          } as never;
        }

        case /^GET \/sessions\/[^/]+\/players$/.test(route):
          return { items: MOCK_PLAYERS } as never;

        case /^POST \/sessions\/[^/]+\/players$/.test(route): {
          const req = body as { nickname: string; avatar: string };
          const newP: Player = {
            id: `p-${Math.random().toString(36).slice(2, 6)}`,
            sessionId: MOCK_SESSION_ID,
            nickname: req.nickname,
            avatar: req.avatar,
            score: 0,
            joinedAt: new Date().toISOString(),
          };
          MOCK_PLAYERS.push({
            id: newP.id,
            nickname: newP.nickname,
            avatar: newP.avatar,
            joinedAt: newP.joinedAt,
          });
          publish({ ...lobbyState, players: [...MOCK_PLAYERS] });
          return { ...newP, sessionToken: `mock-token-${newP.id}` } as never;
        }

        case /^GET \/sessions\/[^/]+\/players\/me$/.test(route):
          return hostPlayer as never;

        case /^PATCH \/sessions\/[^/]+\/players\/me$/.test(route): {
          const req = body as { nickname?: string; avatar?: string };
          if (req.nickname) hostPlayer.nickname = req.nickname;
          if (req.avatar) hostPlayer.avatar = req.avatar;
          return hostPlayer as never;
        }

        case /^POST \/sessions\/[^/]+\/answer$/.test(route): {
          const req = body as
            | { questionId: string; optionId: string }
            | {
                questionId: string;
                matchValue: Array<{ leftPairId: string; rightPairId: string }>;
              };
          const q = questions.find((x) => x.id === req.questionId);
          // Match-type answer: count correctPairs by leftPairId ===
          // rightPairId; award 200 points per correct.
          if (q?.type === 'match' && 'matchValue' in req) {
            const correctPairs = req.matchValue.filter(
              (m) => m.leftPairId === m.rightPairId,
            ).length;
            const pointsAwarded = correctPairs * 200;
            const result: AnswerResult = {
              type: 'match',
              questionId: req.questionId,
              accepted: true,
              correct: null,
              correctPairs,
              pointsAwarded,
              responseMs: 4800,
              newScore: hostPlayer.score + pointsAwarded,
              newRank: 2,
            };
            hostPlayer.score = result.newScore!;
            return result as never;
          }
          const optionId = 'optionId' in req ? req.optionId : undefined;
          const correct =
            (q?.type === 'quiz' || q?.type === 'tf') && optionId
              ? (q.options.find((o) => o.id === optionId)?.isCorrect ?? false)
              : false;
          const result: AnswerResult = {
            type: q?.type ?? 'quiz',
            questionId: req.questionId,
            accepted: true,
            correct,
            pointsAwarded: correct ? 720 : 0,
            responseMs: 4800,
            newScore: hostPlayer.score + (correct ? 720 : 0),
            newRank: 2,
          };
          hostPlayer.score = result.newScore!;
          return result as never;
        }

        case /^GET \/sessions\/[^/]+\/state$/.test(route): {
          // Always return the currently-staged `lobbyState` —
          // `__setMockSessionState(...)` is the single source of
          // truth in Storybook. We used to special-case 'lobby' to
          // return the default `MOCK_LOBBY_STATE`, which silently
          // clobbered every per-story override (empty roster,
          // promoted player, custom theme, etc.).
          return lobbyState as never;
        }

        case /^POST \/sessions\/[^/]+\/advance$/.test(route): {
          if (lobbyState.status === 'lobby') {
            publish(MOCK_QUESTION_OPEN_STATE);
          } else if (lobbyState.status === 'question_open') {
            const q0 = MOCK_QUESTIONS[0]!;
            const q0Opts = 'options' in q0 ? q0.options : [];
            publish({
              ...lobbyState,
              status: 'question_review',
              reveal: {
                type: 'quiz',
                correctOptionId:
                  q0.type === 'quiz'
                    ? q0.options.find((o) => o.isCorrect)!.id
                    : (q0Opts[0]?.id ?? null),
                distribution: q0Opts.map((o) => ({
                  optionId: o.id,
                  count: Math.floor(Math.random() * 5),
                  playerIds: [],
                })),
              },
            });
          } else if (lobbyState.status === 'question_review') {
            const idx = (lobbyState.currentIndex ?? 0) + 1;
            if (idx >= MOCK_QUESTIONS.length) {
              publish({
                ...lobbyState,
                status: 'ended',
                reveal: undefined,
              });
            } else {
              const q = MOCK_QUESTIONS[idx]!;
              const qOpts = 'options' in q ? q.options : [];
              publish({
                ...lobbyState,
                status: 'question_open',
                currentIndex: idx,
                reveal: undefined,
                currentQuestionKiosk: {
                  questionId: q.id,
                  type: q.type,
                  index: idx,
                  total: MOCK_QUESTIONS.length,
                  stem: q.stem,
                  options: qOpts.map((o) => ({
                    id: o.id,
                    position: o.position,
                    text: o.text,
                  })),
                  durationMs: 20000,
                  startedAt: new Date().toISOString(),
                  endsAt: new Date(Date.now() + 20000).toISOString(),
                },
                currentQuestionPlayer: {
                  questionId: q.id,
                  type: q.type,
                  index: idx,
                  total: MOCK_QUESTIONS.length,
                  optionsCount: qOpts.length,
                  options: qOpts.map((o) => ({ id: o.id, position: o.position })),
                  durationMs: 20000,
                  startedAt: new Date().toISOString(),
                  endsAt: new Date(Date.now() + 20000).toISOString(),
                },
              });
            }
          }
          return lobbyState as never;
        }

        case /^POST \/sessions\/[^/]+\/end$/.test(route): {
          publish({ ...lobbyState, status: 'ended' });
          return lobbyState as never;
        }

        case /^GET \/sessions\/[^/]+\/chat$/.test(route):
          return { items: chat } as never;

        case /^POST \/sessions\/[^/]+\/chat$/.test(route): {
          const { text } = body as { text: string };
          const msg: ChatMessage = {
            id: `c-${Math.random().toString(36).slice(2, 8)}`,
            sessionId: MOCK_SESSION_ID,
            playerId: hostPlayer.id,
            nickname: hostPlayer.nickname,
            avatar: hostPlayer.avatar,
            text,
            sentAt: new Date().toISOString(),
          };
          chat = [...chat, msg];
          return msg as never;
        }

        case /^GET \/sessions\/[^/]+\/realtime-auth$/.test(route):
          return REALTIME_AUTH as never;

        default:
          throw new ApiCallError(
            404,
            { code: 'NOT_FOUND', message: `No mock handler for ${route}` },
            `No mock handler for ${route}`,
          );
      }
    },
  };
}
